import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateOrganizationDto } from "../../core/models/dtos/CreateCustomerDto";
import { UpdateOrganizationDto } from "../../core/models/dtos/UpdateCustomerDto";
import { Organization } from "../../core/models/entities/Organization";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { mapToOrganization } from "../models/mappers/mapOrganization";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { PagedResponse } from "../models/responses/pagedResponse";
import { BillingOverviewDto } from "../models/responses/BillingOverviewDto";
import { BillingVendorGroupDto } from "../models/responses/BillingVendorGroupDto";
import { OversizeSheetVendorStateDto } from "../models/responses/OversizeSheetVendorStateDto";
import { OversizeSheetVendorsOverviewDto } from "../models/responses/OversizeSheetVendorsOverviewDto";
import { OversizeSheetLogoCountsDto } from "../models/responses/OversizeSheetLogoCountsDto";
import { DataError } from "../../core/errors/DataError";
import { LogoOptions } from "../../core/models/entities/LogoOptions";
import { mapFromCreateOrganizationDto } from "../models/mapFromCreateOrganizationDto";
import { mapFromUpdateOrganizationDto } from "../models/mapFromUpdateOrganizationDto";
import { CommissionProfile } from "../../core/models/entities/Commission";
import { mapToCommissionProfilePayload } from "../models/mappers/mapCommissions";
import { actAsCacheKey } from "../../contexts/actAsStore";
import { adminSwrListOptions } from "./adminSwr";
import { helperOrgListOptions } from "./helperCache";
import { enviroment } from "../../enviroment";
import { ALL_VENDORS_LOOKUP_QUERY } from "../../utils/models/vendorListQuery";

export interface OrganizationRepository {
  useAllCustomers(query: PageQuery): Resource<PagedResponse<Organization>>;
  useAllVendors(query: PageQuery): Resource<PagedResponse<Organization>>;
  /**
   * EVERY vendor (GET organizations/Vendors?pageSize=0, the API's "no
   * paging"), own cache key ["all-vendors-sheet-size"] - never the paged
   * ["all-vendors", ...] entries the list screens and the start-up prefetch
   * fill (those hold 25 rows when the page size was dropped from the query
   * string). ONE GET per visit of the admin page "Sheet size"; Refresh
   * refetches it; never polled. Flag REACT_APP_VENDOR_LIST_PAGE_SIZE_ENABLED
   * adds all=true to the same call (same key, still ONE GET): complete both
   * with and without the API's VendorListAdminPagedEnabled.
   */
  useAllVendorsUnpaged(): Resource<PagedResponse<Organization>>;
  /**
   * Admin billing overview: which organizations have a billing vendor
   * attached (the invoicing hierarchy). Server-paged/searchable/sortable —
   * DTO consumed as-is. Optional billingVendorOrgId narrows to one vendor's
   * organizations (the drill-down view). keepPreviousData keeps the current
   * page visible while the next one loads. Throws through to an error state
   * until the endpoint is live.
   */
  useBillingOverview(
    query: PageQuery,
    billingVendorOrgId?: number,
  ): Resource<PagedResponse<BillingOverviewDto>>;
  /**
   * The grouped/reversed billing view: one row per billing vendor ("mother")
   * with counts of attached organizations. Same paging/search/error contract
   * as useBillingOverview.
   */
  useBillingVendorGroups(
    query: PageQuery,
  ): Resource<PagedResponse<BillingVendorGroupDto>>;
  prefetchAllCustomers(query: PageQuery): Promise<void>;
  prefetchAllVendors(query: PageQuery): Promise<void>;
  useOrganization(id: number): Resource<Organization>;
  useCustomer(id: number): Resource<Organization>;
  useVendor(id?: number): Resource<Organization>;
  getAllCustomers(): Promise<Organization[]>;
  getAllVendors(): Promise<Organization[]>;
  searchAdminVendors(searchQuery: string): Promise<Organization[]>;
  getOrganizationById(id: number): Promise<Organization>;
  getVendorById(id: number): Promise<Organization>;
  getCustomerById(id: number): Promise<Organization>;
  updateOrganization(dto: UpdateOrganizationDto): Promise<void>;
  createOrganization(
    dto: CreateOrganizationDto,
    companyLogo?: File
  ): Promise<void>;
  deleteOrganization(id: number): Promise<void>;
  updateCustomer(organization: Organization): Promise<Organization>;
  createCustomer(organization: Organization): Promise<Organization>;
  deleteCustomer(organization: Organization): Promise<void>;
  updateOrganizationLogo(
    organizationId: number,
    companyLogo: File
  ): Promise<void>;
  deleteOrganizationLogo(organizationId: number): Promise<void>;
  updateProviderLogo(companyLogo: File): Promise<void>;
  setCommissionProfile(
    orgId: number,
    commissionProfile: CommissionProfile
  ): Promise<void>;
  /**
   * Admin-only: allow/deny the organization managing its own white-label
   * branding (PUT ops/organizations/{orgId}/branding-override). Refetches the
   * org detail afterwards so brandingOverrideEnabled reflects the server.
   */
  setBrandingOverride(orgId: number, enabled: boolean): Promise<void>;
  /**
   * Admin-only (flag REACT_APP_OVERSIZE_SHEET_ADMIN_ENABLED): add/remove a
   * VENDOR org in the admin-managed part of the oversize sheet guard's vendor
   * list (PUT ops/organizations/{orgId}/oversize-sheet-vendor). ONE PUT per
   * click and nothing else on the wire: the PUT's answer is written into the
   * cached ["oversize-sheet-vendors"] list and into the cached logo-options
   * hint's `vendorIds` (the admin's act-as check), and ONLY the org's own
   * detail queries (["organization", id] / ["customer", id] - the checkbox
   * + badge read oversizeSheetVendorEnabled / -FromConfig off them) are
   * refetched when cached. The customer list is never touched (it carries
   * no guard fields).
   */
  setOversizeSheetVendor(
    orgId: number,
    enabled: boolean
  ): Promise<OversizeSheetVendorStateDto>;
  /**
   * Admin-only (flag REACT_APP_OVERSIZE_SHEET_ADMIN_PAGE_ENABLED): the vendors
   * the oversize sheet guard applies to (GET ops/oversize-sheet-vendors),
   * cache key ["oversize-sheet-vendors"]. Refetched on every mount (admin
   * surface: reflect the current server state on entry); between visits a
   * setOversizeSheetVendor writes the PUT's answer into this cache, so the
   * "Sheet size" page and the vendor edit screen always agree without a
   * second call. Throws through to an error state while the API's flag is
   * off (404).
   */
  useOversizeSheetVendors(): Resource<OversizeSheetVendorsOverviewDto>;
  /**
   * Admin-only (flag REACT_APP_OVERSIZE_SHEET_ADMIN_COUNTS_ENABLED, passed
   * as `enabled`): per vendor the number of image logos over the guard's
   * limit (GET ops/oversize-sheet-vendors/logo-counts), cache key
   * ["oversize-sheet-logo-counts"], staleTime 120 s = ONE GET per visit,
   * no retry, never polled. data === null while the API answers 404 (its
   * flag OversizeLogoSheetCountsEnabled off / older API): the caller omits
   * the column, no toast. enabled false = no query, no call.
   */
  useOversizeSheetLogoCounts(
    enabled: boolean
  ): Resource<OversizeSheetLogoCountsDto | null>;
}

export const OVERSIZE_SHEET_VENDORS_KEY = ["oversize-sheet-vendors"] as const;
/**
 * The complete vendor list of the admin page "Sheet size". Its own key on
 * purpose: the paged ["all-vendors", "?pageIndex=1"] entry is 25 rows.
 */
export const ALL_VENDORS_SHEET_SIZE_KEY = ["all-vendors-sheet-size"] as const;
export const OVERSIZE_SHEET_LOGO_COUNTS_KEY = [
  "oversize-sheet-logo-counts",
] as const;
/** The API's 120 s server cache mirrored on the client: ONE GET per visit. */
export const OVERSIZE_SHEET_LOGO_COUNTS_STALE_MS = 120_000;

/** ONLY the API's 404 (its flag off) means "no counts"; anything else is an error. */
export function isOversizeSheetLogoCountsNotActive(error: unknown): boolean {
  return error instanceof DataError && error.errorStatus?.code === 404;
}

/**
 * Pure cache updater for the ["oversize-sheet-vendors"] list after a
 * successful PUT: the API's answer {enabled (DB part), fromConfig, effective,
 * vendorIds (union)} is the honest server state for that org, so the row is
 * upserted (effective) or removed (not effective) and the id lists follow.
 * name/navNumber of a NEW row are null (the page reads them off the vendor
 * list anyway); an existing row keeps its own. undefined in = undefined out
 * (nothing cached = nothing to update, no phantom entry).
 */
export function applyOversizeSheetVendorState(
  previous: OversizeSheetVendorsOverviewDto | undefined,
  orgId: number,
  state: OversizeSheetVendorStateDto
): OversizeSheetVendorsOverviewDto | undefined {
  if (!previous) return undefined;
  const existing = previous.organizations.find(
    (o) => o.organizationId === orgId
  );
  const others = previous.organizations.filter(
    (o) => o.organizationId !== orgId
  );
  const organizations = state.effective
    ? [
        ...others,
        {
          organizationId: orgId,
          name: existing?.name ?? null,
          navNumber: existing?.navNumber ?? null,
          fromConfig: state.fromConfig,
          fromAdmin: state.enabled,
        },
      ]
    : others;
  const dbWithout = previous.dbVendorIds.filter((id) => id !== orgId);
  return {
    ...previous,
    dbVendorIds: state.enabled
      ? [...dbWithout, orgId].sort((a, b) => a - b)
      : dbWithout,
    vendorIds: [...state.vendorIds].sort((a, b) => a - b),
    organizations,
  };
}

export function useOrganizationRepository(): OrganizationRepository {
  const apiClient = useApiClient();

  const queryClient = useQueryClient();
  // Act-as-vendor: getAllCustomers is scoped by ApiClient (actAsOrgId param),
  // so the impersonation scope must be part of the customer-list cache keys —
  // otherwise the admin's global rows are served from cache after switching
  // identity. 0 = not impersonating. Same shared function as Logo-/Design-/
  // OrderRepository (see OrderRepository for the full rationale).
  const actAsKey = actAsCacheKey;
  async function invalidateCustomer(id: Organization["id"]) {
    await queryClient.refetchQueries({
      queryKey: ["organization", id],
      type: "all",
      refetchPage: () => true,
    });
    await queryClient.refetchQueries({
      queryKey: ["customer", id],
      type: "all",
      refetchPage: () => true,
    });
    await invalidateCustomers();
  }

  async function invalidateCustomers() {
    await queryClient.refetchQueries({
      queryKey: ["all-customers"],
      type: "all",
      refetchPage: () => true,
    });
  }

  function useOrganization(id: Organization["id"]): Resource<Organization> {
    return useQuery(["organization", id], () =>
      apiClient.getOrganization(id).then((data) => mapToOrganization(data))
    );
  }

  function useCustomer(id: Organization["id"]): Resource<Organization> {
    return useQuery(["customer", id], () =>
      apiClient.getCustomer(id).then((data) => mapToOrganization(data))
    );
  }

  function useVendor(id?: Organization["id"]): Resource<Organization> {
    return useQuery(["vendor", id], () => {
      if (!id) {
        return null;
      }
      return apiClient.getVendor(id).then((data) => mapToOrganization(data));
    });
  }

  async function getOrganizationById(
    id: Organization["id"]
  ): Promise<Organization> {
    const response = await apiClient.getOrganization(id);
    return mapToOrganization(response);
  }

  async function getVendorById(id: Organization["id"]): Promise<Organization> {
    return apiClient.getVendor(id).then((data) => mapToOrganization(data));
  }

  async function getCustomerById(
    id: Organization["id"]
  ): Promise<Organization> {
    return apiClient.getCustomer(id).then((data) => mapToOrganization(data));
  }

  // Admin perf FE-4 (flag): the two org lists every list screen gates on
  // get a 60 s TTL, a 30 min cache and revalidate on entry; own create/
  // delete/update mutations below invalidate them. Off = default options.
  function useAllCustomers(
    query: PageQuery
  ): Resource<PagedResponse<Organization>> {
    return useQuery(
      ["all-customers", actAsKey(), toQueryParam(query)],
      () =>
        apiClient.getAllCustomers(query).then((data) => ({
          ...data,
          results: data.results.map(mapToOrganization),
        })),
      { ...helperOrgListOptions() }
    );
  }

  function useAllVendors(
    query: PageQuery
  ): Resource<PagedResponse<Organization>> {
    return useQuery(
      ["all-vendors", toQueryParam(query)],
      () =>
        apiClient.getAllVendors(query).then((data) => ({
          ...data,
          results: data.results.map(mapToOrganization),
        })),
      { ...helperOrgListOptions() }
    );
  }

  // Admin perf FE-4 (flag): mark both helper lists stale after an own org
  // mutation (active queries refetch now, the next mount revalidates).
  // Off = no-op, exactly today's (missing) invalidation.
  async function invalidateHelperOrgLists(): Promise<void> {
    if (!enviroment.adminPerfHelperCacheEnabled) return;
    await queryClient.invalidateQueries({ queryKey: ["all-customers"] });
    await queryClient.invalidateQueries({ queryKey: ["all-vendors"] });
    // The "Sheet size" page's unpaged vendor list drifts otherwise (own key,
    // not under "all-vendors"); inactive = 0 calls, mounted = refetch now.
    await queryClient.invalidateQueries({
      queryKey: [...ALL_VENDORS_SHEET_SIZE_KEY],
    });
  }

  function useAllVendorsUnpaged(): Resource<PagedResponse<Organization>> {
    return useQuery(
      [...ALL_VENDORS_SHEET_SIZE_KEY],
      () =>
        (enviroment.vendorListPageSizeEnabled
          ? apiClient.getAllVendorsUnpaged(true)
          : apiClient.getAllVendorsUnpaged()
        ).then((data) => ({
          ...data,
          results: data.results.map(mapToOrganization),
        })),
      {
        // Admin surface, same rhythm as the guard list below: ONE GET on
        // every entry (a vendor created meanwhile is seen), never polled.
        staleTime: 0,
        refetchOnMount: true,
      }
    );
  }

  function useOversizeSheetVendors(): Resource<OversizeSheetVendorsOverviewDto> {
    return useQuery(
      [...OVERSIZE_SHEET_VENDORS_KEY],
      () => apiClient.getOversizeSheetVendors(),
      {
        // Admin surface: ONE GET on every entry so another admin's change is
        // seen; the app default (refetchOnMount false) would keep a stale
        // list for the whole session. Never polled.
        staleTime: 0,
        refetchOnMount: true,
      }
    );
  }

  function useOversizeSheetLogoCounts(
    enabled: boolean
  ): Resource<OversizeSheetLogoCountsDto | null> {
    return useQuery(
      [...OVERSIZE_SHEET_LOGO_COUNTS_KEY],
      () =>
        apiClient.getOversizeSheetLogoCounts().catch((error: unknown) => {
          // 404 = the API's flag is off: an honest "no counts" (null) the
          // page renders as "no column", cached like an answer so the next
          // 120 s cost nothing either. Other failures stay errors.
          if (isOversizeSheetLogoCountsNotActive(error)) return null;
          throw error;
        }),
      {
        // Strictly true: react-query reads undefined as "enabled".
        enabled: enabled === true,
        staleTime: OVERSIZE_SHEET_LOGO_COUNTS_STALE_MS,
        refetchOnMount: true,
        // Refresh on the page is the retry; the default 3 retries would
        // turn one failed visit into 4 GETs.
        retry: false,
      }
    );
  }

  function useBillingOverview(
    query: PageQuery,
    billingVendorOrgId?: number
  ): Resource<PagedResponse<BillingOverviewDto>> {
    return useQuery(
      // null (not undefined) in the key: undefined segments are dropped by
      // React Query's key hashing, which would collide the unfiltered list
      // with a drill-down list.
      ["billing-overview", toQueryParam(query), billingVendorOrgId ?? null],
      () => apiClient.getBillingOverview(query, billingVendorOrgId),
      {
        // Admin surface — reflect the current server state on entry, and keep
        // the previous page visible while the next one loads.
        staleTime: 0,
        keepPreviousData: true,
        // Admin perf FE-3 (flag): paint from cache + one background refetch.
        ...adminSwrListOptions(),
      }
    );
  }

  function useBillingVendorGroups(
    query: PageQuery
  ): Resource<PagedResponse<BillingVendorGroupDto>> {
    return useQuery(
      ["billing-vendors", toQueryParam(query)],
      () => apiClient.getBillingVendorGroups(query),
      {
        // Same admin-surface behavior as useBillingOverview.
        staleTime: 0,
        keepPreviousData: true,
        ...adminSwrListOptions(),
      }
    );
  }

  // Cache-warming twins of useAllCustomers/useAllVendors (same key and
  // queryFn, default options) so the key construction lives in one place.
  // Fail-silent: on a miss the hooks fetch as they do today.
  function prefetchAllCustomers(query: PageQuery): Promise<void> {
    return queryClient.prefetchQuery(["all-customers", actAsKey(), toQueryParam(query)], () =>
      apiClient.getAllCustomers(query).then((data) => ({
        ...data,
        results: data.results.map(mapToOrganization),
      }))
    );
  }

  function prefetchAllVendors(query: PageQuery): Promise<void> {
    return queryClient.prefetchQuery(["all-vendors", toQueryParam(query)], () =>
      apiClient.getAllVendors(query).then((data) => ({
        ...data,
        results: data.results.map(mapToOrganization),
      }))
    );
  }

  async function getAllCustomers(): Promise<Organization[]> {
    const response = await apiClient.getAllCustomers({
      page: { size: 0, index: 0 },
    } as PageQuery);
    return response.results.map(mapToOrganization);
  }

  async function getAllVendors(): Promise<Organization[]> {
    // No caller in src today (skeptic 27/9): with the flag the "all vendors"
    // promise asks for the same explicit all=true list as the hooks, so it
    // can never silently become the API's default page; off = unchanged.
    const response = await apiClient.getAllVendors(
      enviroment.vendorListPageSizeEnabled
        ? ALL_VENDORS_LOOKUP_QUERY
        : ({ page: { size: 0, index: 0 } } as PageQuery),
    );
    return response.results.map(mapToOrganization);
  }

  async function searchAdminVendors(
    searchQuery: string
  ): Promise<Organization[]> {
    const response = await apiClient.searchAdminVendors(searchQuery);
    return response.map(mapToOrganization);
  }

  async function updateOrganization(
    dto: UpdateOrganizationDto,
    companyLogo?: File
  ): Promise<void> {
    return await apiClient
      .updateOrganization(dto.id, mapFromUpdateOrganizationDto(dto))
      .then(async (res) => {
        if (res.isVendor && companyLogo) {
          await apiClient.uploadCompanyLogo(res.id, companyLogo);
        }
        await invalidateHelperOrgLists();
      });
  }

  async function updateOrganizationLogo(
    organizationId: Organization["id"],
    companyLogo: File
  ): Promise<void> {
    return await apiClient.uploadCompanyLogo(organizationId, companyLogo);
  }

  async function deleteOrganizationLogo(
    organizationId: Organization["id"]
  ): Promise<void> {
    return await apiClient.deleteCompanyLogo(organizationId);
  }

  async function updateProviderLogo(companyLogo: File): Promise<void> {
    return await apiClient.uploadProviderLogo(companyLogo);
  }

  function createCustomer(organization: Organization): Promise<Organization> {
    return apiClient
      .createCustomer(organization)
      .then(mapToOrganization)
      .then(async (created) => {
        await invalidateHelperOrgLists();
        return created;
      });
  }

  function updateCustomer(organization: Organization): Promise<Organization> {
    return apiClient
      .updateCustomer(organization)
      .then(mapToOrganization)
      .finally(() => invalidateCustomer(organization.id));
  }

  function deleteCustomer(organization: Organization): Promise<void> {
    return apiClient
      .deleteCustomer(organization)
      .then(() => invalidateHelperOrgLists());
  }

  async function createOrganization(
    dto: CreateOrganizationDto,
    companyLogo?: File
  ): Promise<void> {
    return await apiClient
      .createOrganization(mapFromCreateOrganizationDto(dto))
      .then(async (res) => {
        if (res.isVendor && companyLogo) {
          await apiClient.uploadCompanyLogo(res.id, companyLogo);
        }
        await invalidateHelperOrgLists();
      });
  }

  function deleteOrganization(id: number): Promise<void> {
    return apiClient
      .deleteOrganization(id)
      .then(() => invalidateHelperOrgLists());
  }

  async function setCommissionProfile(
    orgId: number,
    commissionProfile: CommissionProfile
  ): Promise<void> {
    if (
      !commissionProfile.overAll &&
      !commissionProfile.additionalFees &&
      !commissionProfile.heating &&
      commissionProfile.methods.length === 0 &&
      commissionProfile.typeOnly.length === 0
    ) {
      await apiClient.deleteCommissionProfile(orgId);
    } else {
      // Normalize method rules to the wire shape the API price engine
      // matches (cm2 buckets as maxSizeCM2 + priceType, not as the
      // "100 cm²" display label the editors key rows on).
      await apiClient.setCommissionProfile(
        orgId,
        mapToCommissionProfilePayload(commissionProfile)
      );
    }
    await invalidateCustomer(orgId);
  }

  async function setBrandingOverride(
    orgId: number,
    enabled: boolean
  ): Promise<void> {
    await apiClient.setBrandingOverride(orgId, enabled);
    // The edit screens read brandingOverrideEnabled off the org detail query
    // (["customer", id] — used for vendors too), so refetch it for freshness.
    await invalidateCustomer(orgId);
  }

  async function setOversizeSheetVendor(
    orgId: number,
    enabled: boolean
  ): Promise<OversizeSheetVendorStateDto> {
    const state = await apiClient.setOversizeSheetVendor(orgId, enabled);
    // The vendor edit screen reads oversizeSheetVendorEnabled / -FromConfig
    // off the org detail queries (["customer", id] - used for vendors too -
    // and ["organization", id]), so refetch THOSE (type "all": the edit
    // screen may hold them inactive while the admin is on the "Sheet size"
    // page). NOT invalidateCustomer: that also refetches the whole
    // ["all-customers"] list (size 0 = every customer), which carries no
    // guard fields - on a page meant for ticking 129+ vendors in a row that
    // would be one customer-list download per click.
    await queryClient.refetchQueries({
      queryKey: ["organization", orgId],
      type: "all",
      refetchPage: () => true,
    });
    await queryClient.refetchQueries({
      queryKey: ["customer", orgId],
      type: "all",
      refetchPage: () => true,
    });
    // The oversize guard's hint (vendorIds for the admin's act-as check)
    // rides on the cached GET logos/options/all, which is an ACTIVE query
    // app-wide (PreloadData) - invalidating it would refetch the whole
    // options catalog per click. The PUT's `vendorIds` IS the union the API
    // would answer, so it is patched into the cached hint instead (0 calls).
    queryClient.setQueryData<LogoOptions | undefined>(["logo-options"], (previous) =>
      previous?.oversizeSheetGuard
        ? {
            ...previous,
            oversizeSheetGuard: {
              ...previous.oversizeSheetGuard,
              vendorIds: [...state.vendorIds].sort((a, b) => a - b),
            },
          }
        : undefined
    );
    // The admin page "Sheet size" reads ["oversize-sheet-vendors"]: the PUT's
    // answer IS the server state for this org, so it is written into that
    // cache (0 extra calls) - both places agree at once, whichever one the
    // admin clicked in. Nothing cached = nothing written.
    queryClient.setQueryData<OversizeSheetVendorsOverviewDto | undefined>(
      [...OVERSIZE_SHEET_VENDORS_KEY],
      (previous) => applyOversizeSheetVendorState(previous, orgId, state)
    );
    return state;
  }

  return {
    useAllVendors,
    useAllVendorsUnpaged,
    useAllCustomers,
    useBillingOverview,
    useBillingVendorGroups,
    prefetchAllCustomers,
    prefetchAllVendors,
    useOrganization,
    useCustomer,
    useVendor,
    getAllVendors,
    getAllCustomers,
    searchAdminVendors,
    getOrganizationById,
    getCustomerById,
    getVendorById,
    updateOrganization,
    createOrganization,
    deleteOrganization,
    updateOrganizationLogo,
    deleteOrganizationLogo,
    updateProviderLogo,
    createCustomer,
    deleteCustomer,
    updateCustomer,
    setCommissionProfile,
    setBrandingOverride,
    setOversizeSheetVendor,
    useOversizeSheetVendors,
    useOversizeSheetLogoCounts,
  };
}
